import React, { useCallback, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";

import { Button, HookFormInputGroup } from "components";

import { HookFormInputGroupProps } from "../hookFormInputGroup";

import { ReactComponent as CancelIcon } from "static/images/cancel.svg";
import { ReactComponent as CheckDone } from "static/images/checked.svg";
import { ReactComponent as EditIcon } from "static/images/pencil.svg";
import styles from "./editableFieldWithHookInput.module.scss";

type EditableFieldWithHookInputProps = {
	componentProps: HookFormInputGroupProps;
	onSubmit?: Function;
	isEditable?: boolean;
	classNameViewContainer?: string;
	classNameViewField?: string;
};

const EditableFieldWithHookInput = ({
	componentProps,
	onSubmit,
	isEditable = true,
	classNameViewContainer = "",
	classNameViewField = "",
}: EditableFieldWithHookInputProps) => {
	const [initValue, setInitValue] = useState<string | undefined>();
	const [isEditMode, setIsEditMode] = useState<boolean>(false);

	const { handleSubmit, getValues, setValue, watch } = useFormContext();

	const fieldName = componentProps.name;

	const currentValue = watch(fieldName);

	const handleEditField = useCallback(
		e => {
			e.stopPropagation();
			setInitValue(getValues(fieldName));
			setIsEditMode(true);
		},
		[fieldName, getValues],
	);

	const handleCancelEdit = useCallback(
		e => {
			e.stopPropagation();
			setIsEditMode(false);
			initValue !== undefined && setValue(fieldName, initValue);
		},
		[fieldName, initValue, setValue],
	);

	const handleSaveField = useCallback(
		e => {
			e.stopPropagation();
			handleSubmit(values => {
				onSubmit?.(values[componentProps.name], () => {
					setInitValue(values[componentProps.name]);
					setIsEditMode(false);
				});
			})();
		},
		[handleSubmit, onSubmit, componentProps.name],
	);

	return (
		<>
			{isEditable && isEditMode ? (
				<>
					<HookFormInputGroup {...componentProps} />
					<div className={styles.buttonsContainer}>
						<Button
							onClick={handleSaveField}
							icon={<CheckDone />}
							btnIcon
							variant="secondary"
							size="sm"
							title="Save"
						/>
						<Button
							onClick={handleCancelEdit}
							icon={<CancelIcon />}
							btnIcon
							variant="secondary-danger"
							size="sm"
							title="Cancel"
						/>
					</div>
				</>
			) : (
				<Row className={`form-group ${classNameViewContainer}`}>
					{componentProps.label && (
						<label className="form-label col-form-label col-sm-6">
							{componentProps.label}
						</label>
					)}
					<Col>
						<div className={classNameViewField}>
							{initValue === undefined ? currentValue : initValue}
						</div>
						{isEditable && (
							<Button
								onClick={handleEditField}
								btnIcon
								icon={<EditIcon />}
								variant="secondary"
								size="sm"
								title="Edit"
							/>
						)}
					</Col>
				</Row>
			)}
		</>
	);
};

export default EditableFieldWithHookInput;
